import * as React from 'react';
import { makeStyles, mergeClasses, shorthands, Text, tokens } from '@fluentui/react-components';
import {
    ChartMultiple24Regular,
    CheckboxChecked24Regular,
    ChatMultiple24Regular,
    RadioButton24Regular,
    Star24Regular,
    TextAlignLeft24Regular,
} from '@fluentui/react-icons';
import { PollQuestionType } from '../../services/DataService';
import { QUESTION_TYPE_OPTIONS } from '../../lib/pollLabels';

const useStyles = makeStyles({
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: '12px',
        '@media (max-width: 767px)': {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '10px',
        },
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '10px',
        minHeight: '124px',
        padding: '16px',
        textAlign: 'left',
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusLarge,
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        cursor: 'pointer',
        transitionProperty: 'border-color, background-color, box-shadow, transform',
        transitionDuration: tokens.durationFaster,
        transitionTimingFunction: tokens.curveEasyEase,
        ':hover': {
            ...shorthands.borderColor(tokens.colorBrandStroke1),
            boxShadow: tokens.shadow4,
        },
        ':active': {
            transform: 'scale(0.98)',
        },
        ':focus-visible': {
            outline: `2px solid ${tokens.colorStrokeFocus2}`,
            outlineOffset: '2px',
        },
        '@media (max-width: 767px)': {
            padding: '12px',
            minHeight: '108px',
        },
    },
    cardSelected: {
        ...shorthands.borderColor(tokens.colorBrandBackground),
        backgroundColor: tokens.colorBrandBackground2,
        boxShadow: `inset 0 0 0 1px ${tokens.colorBrandBackground}`,
    },
    icon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        borderRadius: tokens.borderRadiusMedium,
        color: tokens.colorBrandForeground1,
        backgroundColor: tokens.colorBrandBackground2,
    },
    iconSelected: {
        color: tokens.colorNeutralForegroundOnBrand,
        backgroundColor: tokens.colorBrandBackground,
    },
    title: {
        fontWeight: tokens.fontWeightSemibold,
    },
    description: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
});

export const QUESTION_TYPE_ICONS: Record<PollQuestionType, React.ReactElement> = {
    [PollQuestionType.Rating]: <Star24Regular />,
    [PollQuestionType.SingleChoice]: <RadioButton24Regular />,
    [PollQuestionType.MultipleChoice]: <CheckboxChecked24Regular />,
    [PollQuestionType.Text]: <TextAlignLeft24Regular />,
    [PollQuestionType.Nps]: <ChartMultiple24Regular />,
    [PollQuestionType.WordCloud]: <ChatMultiple24Regular />,
};

export interface IQuestionTypePickerProps {
    value: PollQuestionType;
    onChange: (type: PollQuestionType) => void;
}

/** Radio-like grid of question types for the quick wizard. */
const QuestionTypePicker: React.FC<IQuestionTypePickerProps> = ({ value, onChange }) => {
    const styles = useStyles();
    return (
        <div className={styles.grid} role="radiogroup" aria-label="Тип вопроса">
            {QUESTION_TYPE_OPTIONS.map((option) => {
                const selected = option.value === value;
                return (
                    <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        className={mergeClasses(styles.card, selected && styles.cardSelected)}
                        onClick={() => onChange(option.value)}
                    >
                        <span className={mergeClasses(styles.icon, selected && styles.iconSelected)}>
                            {QUESTION_TYPE_ICONS[option.value]}
                        </span>
                        <Text className={styles.title}>{option.label}</Text>
                        <Text className={styles.description}>{option.description}</Text>
                    </button>
                );
            })}
        </div>
    );
};

export default QuestionTypePicker;
